<template>
  <div>
    <v-btn primary class="mb-3" @click="onClickAdd" :disabled="loading">
      <v-icon>mdi-plus</v-icon>
      {{ $t("button.add") }}
    </v-btn>
    <edit-dialog
      v-model="dialogModel"
      @hide="dialog = $event"
      :show="dialog"
      v-if="dialog"
    ></edit-dialog>
    <v-text-field
      v-model="search"
      append-icon="mdi-magnify"
      label="Search"
      single-line
      hide-details
      class="my-3"
    ></v-text-field>
    <v-data-table
      :search="search"
      :class="{ table: !loading }"
      :headers="selectedHeaders"
      :items="list"
      :items-per-page="50"
      :loading="loading"
      :loading-text="$t('projects.annotationClasses.table.loading')"
      :no-data-text="$t('projects.annotationClasses.table.empty')"
      disable-pagination
      hide-default-footer
      filterable
      @click:row="onClick"
    >
      <template v-slot:header.actions="{}">
        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-icon
              v-bind="attrs"
              v-on="on"
              small
              @click.stop.prevent="showTableColumnSelector = true"
            >
              mdi-pencil
            </v-icon>
          </template>
          <span>
            {{ $t("components.tableColumnSelector.tooltip") }}
          </span>
        </v-tooltip>
      </template>
      <template v-slot:item.annotation_geometry="{ item }">
          <span>
            {{ $t("projects.annotationClasses.classes." + item.annotation_geometry.toLowerCase()) }}
          </span>
      </template>
      <template v-slot:item.description="{ item }">
        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <span v-bind="attrs" v-on="on">{{
              item.description | truncate
            }}</span>
          </template>
          <span>{{ item.description }}</span>
        </v-tooltip>
      </template>
      <template v-slot:item.color="{ item }">
        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <div
              v-bind="attrs"
              v-on="on"
              class="text-center"
              :style="style(item.color)"
            ></div>
          </template>
          <span>{{ item.color }}</span>
        </v-tooltip>
      </template>
      <template v-slot:item.fill_color="{ item }">
        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <div
              v-bind="attrs"
              v-on="on"
              class="text-center"
              :style="style(item.fill_color)"
            ></div>
          </template>
          <span>{{ item.fill_color }}</span>
        </v-tooltip>
      </template>
      <template v-slot:item.actions="{ item }">
        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-icon
              :disabled="loading"
              v-bind="attrs"
              v-on="on"
              small
              @click.stop.prevent="onClickDeleteAnnotationClass(item)"
            >
              mdi-delete
            </v-icon>
          </template>
          <span>
            {{ $t("projects.annotationClasses.table.delete") }}
          </span>
        </v-tooltip>
      </template>
    </v-data-table>
    <DialogDelete
      v-model="showDeleteConfirmation"
      :item="itemDelete"
      @delete="onDelete"
    ></DialogDelete>
    <TableColumnSelector
      :show="showTableColumnSelector"
      @hide="showTableColumnSelector = false"
      v-model="headers"
      :skip="['actions']"
    ></TableColumnSelector>
  </div>
</template>

<script>
import { mapGetters, mapState } from "vuex";
import { DELETE, LIST, LOAD } from "@/store/modules/annotationClass";
import EditDialog from "@/components/projects/annotation-class/Dialog";
import DialogDelete from "@/components/DialogDelete";
import {
  TABLE_HEADER_ANNOTATION_CLASS_LIST,
  TABLE_HEADER_PROJECT_LIST
} from "@/constants";
import AddDialog from "@/components/projects/AddDialog";
import TableColumnSelector from "@/components/TableColumnSelector";

export default {
  components: { EditDialog, DialogDelete, TableColumnSelector },
  props: {
    project: { required: true }
  },
  data: () => ({
    annotationClasses: [],
    search: null,
    dialog: false,
    dialogModel: {},
    showDeleteConfirmation: false,
    itemDelete: null,
    showTableColumnSelector: false
  }),
  computed: {
    ...mapState({
      loading: state => state.annotationClass.loading
    }),
    ...mapGetters({ list: LIST }),
    selectedHeaders() {
      return this.headers.filter(e => e.visible);
    },
    headers() {
      return [
        { text: this.$t("projects.annotationClasses.table.id"), value: "id", visible: false },
        { text: this.$t("projects.annotationClasses.table.classname"), value: "class_name", visible: true },
        { text: this.$t("projects.annotationClasses.table.type"), value: "annotation_geometry", visible: true },
        { text: this.$t("projects.annotationClasses.table.description"), value: "description", visible: true },
        { text: this.$t("projects.annotationClasses.table.strokeColor"), value: "color", visible: true },
        { text: this.$t("projects.annotationClasses.table.fillColor"), value: "fill_color", visible: true },
        {
          text: this.$t("projects.annotationClasses.table.numberOfAnnotations"),
          value: "numberOfAnnotations",
          visible: false
        },
        { text: this.$t("projects.annotationClasses.table.actions"), value: "actions", filterable: false, visible: true }
      ]
    },
    notifyText() {
      return this.$t("projects.annotationClasses.table.actionCompleted");
    }
  },
  watch: {
    headers: {
      deep: true,
      handler(val) {
        this.$dp.store(TABLE_HEADER_ANNOTATION_CLASS_LIST, val, true);
      }
    }
  },
  methods: {
    style(color) {
      return {
        height: "10px",
        width: "10px",
        "background-color": color
      };
    },
    onClick(annotationClass) {
      if (this.loading) return;
      this.dialogModel = annotationClass;
      this.dialog = true;
    },
    onClickAdd() {
      this.dialogModel = {};
      this.dialog = true;
    },
    async onDelete(item) {
      await this.$store.dispatch(DELETE, item);
      this.$notify({
        text: this.notifyText,
        type: "success"
      });
    },
    async onClickDeleteAnnotationClass(item) {
      this.showDeleteConfirmation = true;
      this.itemDelete = item;
    }
  },
  created() {
    this.$store.dispatch(LOAD);
  }
};
</script>

<style lang="css" scoped></style>
